import type { MutableRefObject, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { breakpoints, vars } from '@etg/wings';
import { Link, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { css, cx, resetButtonStyle } from '@eti/styles';
import { useScroll, useScrollTo } from '@eti/utils';
import { ContentWrapper } from '@eti/components';
import { useSnowplowTracking } from '@eti/providers';
import isEmpty from 'lodash/isEmpty';
import { routes } from '../../../constants/routesAndApisConstants';
import { focusStyles } from '../../../utils/styleUtils';
import ScrollableTabs from './ScrollableTabs';

const contentWrapperStyles = css`
  position: relative;
`;

const wrapperStyles = (isScrolledUp: boolean) => css`
  background: #fff;
  border-bottom: 1px solid ${vars.colors.divider};
  border-top: 1px solid ${vars.colors.divider};
  box-shadow:
    rgb(0 0 0 / 4%) 0 0 1px 0,
    rgb(0 0 0 / 6%) 0 0 2px 0,
    rgb(0 0 0 / 4%) 0 4px 8px 0;
  position: sticky;
  top: ${isScrolledUp ? '57px' : 0};
  transition: top 0.2s ease-in-out;
  width: 100%;
  z-index: 11;

  @media (min-width: ${breakpoints._768}) {
    top: ${isScrolledUp ? '68px' : 0};
  }
`;

const tabStyles = (active: boolean) => css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  background-color: #fff;
  border-bottom: 2px solid ${active ? vars.colors.inputs.main : 'transparent'};
  color: ${active ? vars.colors.inputs.main : '#737373'};
  display: block;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 700;
  padding: 16px 10px;
  text-decoration: none;

  &:hover {
    background-color: ${vars.colors.inputs.hover};
    color: ${vars.colors.inputs.main};
    cursor: ${!active && 'pointer'};
  }

  &:focus-visible {
    outline-offset: -0.1875rem;
  }
`;

export type RoutedTabsContextType = {
  tabHeading1Ref: MutableRefObject<HTMLHeadingElement | null>;
};
const RoutedTabsContext = createContext<RoutedTabsContextType | null>(null);

type RouteKeys = keyof typeof routes;
export type RouteValues = (typeof routes)[RouteKeys];
export interface TabItem {
  content: ReactNode;
  heading: { dataTestId: string; text: string };
  route: {
    exact?: boolean;
    path: RouteValues;
  };
  snowplowLabel: string;
}
interface RoutedTabsProps {
  tabItems: TabItem[];
  tabNotFoundFallbackUrl: string;
}

const RoutedTabs = ({ tabItems, tabNotFoundFallbackUrl = routes.START }: RoutedTabsProps) => {
  const location = useLocation();
  const [isFirstRender, setIsFirstRender] = useState(true);
  const tabHeading1Ref = useRef<HTMLHeadingElement | null>(null);

  const { isScrollingUp } = useScroll({
    throttleTime: 100,
    useCaptionOnScrollListener: true,
  });
  const { scrollVertical } = useScrollTo();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();

  const activeTabIndex = useMemo<number | null>(() => {
    const activeTabIdx = tabItems.findIndex((tab) => tab.route.path === location.pathname);

    return activeTabIdx !== -1 ? activeTabIdx : null;
  }, [location.pathname, tabItems]);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    } else if (!location.hash && !location.search && tabHeading1Ref.current) {
      tabHeading1Ref.current.focus();
    }
  }, [scrollVertical, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSnowplowTrackingEnabled && activeTabIndex !== null && isFirstRender) {
      const activeTab = tabItems[activeTabIndex];
      sendTrackEvent({
        category: activeTab.snowplowLabel,
        action: 'Seen',
        label: 'Page',
      });
    }
  }, [activeTabIndex, isFirstRender, isSnowplowTrackingEnabled, sendTrackEvent, tabItems]);

  const handleSnowplowOnRoutedTab = (snowplowLabel: string) => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: snowplowLabel,
        action: 'Seen',
        label: 'Page',
      });
      sendTrackEvent({
        category: 'Tab Menu',
        action: 'Click',
        label: snowplowLabel,
      });
    }
  };

  return (
    <RoutedTabsContext.Provider value={{ tabHeading1Ref }}>
      <div className={wrapperStyles(isScrollingUp)} id="routed-tabs">
        <ContentWrapper>
          <ScrollableTabs isNavigation selectedIndex={activeTabIndex}>
            <>
              {tabItems.map(
                ({ heading: { dataTestId, text }, route: { path }, snowplowLabel }, index) => {
                  const isActivePath = location.pathname === path;
                  return (
                    <ScrollableTabs.Tab
                      aria-current={isActivePath}
                      as={Link}
                      className={cx(focusStyles, tabStyles(isActivePath))}
                      data-testid={dataTestId}
                      index={index}
                      key={dataTestId}
                      onClick={() => handleSnowplowOnRoutedTab(snowplowLabel)}
                      to={path}
                    >
                      {text}
                    </ScrollableTabs.Tab>
                  );
                },
              )}
            </>
          </ScrollableTabs>
        </ContentWrapper>
      </div>
      <Switch>
        {tabItems.map(({ content, route: { exact = false, path } }) => (
          <Route exact={exact} key={path} path={path}>
            <div className={contentWrapperStyles}>{content}</div>
          </Route>
        ))}
        <Redirect to={tabNotFoundFallbackUrl} />
      </Switch>
    </RoutedTabsContext.Provider>
  );
};

const useRoutedTabs = (): RoutedTabsContextType => {
  const context = useContext(RoutedTabsContext);

  if (isEmpty(context)) {
    throw new Error('useRoutedTabs must be used within an RoutedTabsProvider');
  }

  return { ...context };
};

export default RoutedTabs;
export { useRoutedTabs };
